// @ts-check
/**
 * Shared builder for the Bunnings third-party share-cart URL.
 *
 * Serialises cart line items as `items=SKU:qty,SKU:qty,…` on the given base
 * URL. Lines with a blank SKU or non-positive quantity are skipped. Used by
 * the kiosk QR flow (sections/bunnings-kiosk-main.liquid), the TESO checkout
 * redirect (assets/teso-checkout.js) and the 3rd Party Cart QR checkout
 * (assets/third-party-cart-checkout.js).
 *
 * Exposed both as an ES module export and on `window.BunningsShareCart` so
 * classic inline scripts (kiosk) can consume it.
 *
 * See docs/business-rules/teso-promotion.md.
 */

const DEFAULT_BASE_URL = 'https://bunnings.com.au/share-cart';

/**
 * Normalised checkout type for a cart line (from /cart.js).
 *
 * Prefers the generic `_checkout_type` line item property stamped at
 * add-to-cart. Falls back to the legacy `_teso === 'true'` marker → `'teso'`.
 * Blank / undefined / "undefined" / "null" / unknown → `'regular'`.
 *
 * @param {{ properties?: Record<string, string> | null }} item
 * @returns {'regular' | 'teso' | 'third_party_cart' | string}
 */
export function getLineCheckoutType(item) {
  const raw = String(item?.properties?._checkout_type ?? '')
    .trim()
    .toLowerCase();
  if (raw && raw !== 'undefined' && raw !== 'null') return raw;
  if (String(item?.properties?._teso ?? '') === 'true') return 'teso';
  return 'regular';
}

/**
 * Whether a cart line (from /cart.js) is a TESO promotion line.
 *
 * @param {{ properties?: Record<string, string> | null }} item
 * @returns {boolean}
 */
export function isTesoLine(item) {
  return getLineCheckoutType(item) === 'teso';
}

/**
 * Whether a cart line (from /cart.js) is a 3rd Party Cart line.
 *
 * @param {{ properties?: Record<string, string> | null }} item
 * @returns {boolean}
 */
export function isThirdPartyCartLine(item) {
  return getLineCheckoutType(item) === 'third_party_cart';
}

/**
 * Lines that `buildShareCartUrl` would silently skip: blank SKU or a
 * non-positive quantity. Callers that must hand off every line check this first.
 *
 * @template {{ sku?: string | null, quantity?: number }} T
 * @param {T[]} items
 * @returns {T[]}
 */
export function findUnserialisableLines(items) {
  return items.filter((item) => !String(item.sku ?? '').trim() || !(Math.floor(Number(item.quantity)) > 0));
}

/**
 * Whether a cart line uses a non-Shopify (external) checkout.
 *
 * @param {{ properties?: Record<string, string> | null }} item
 * @returns {boolean}
 */
export function isExternalCheckoutLine(item) {
  const type = getLineCheckoutType(item);
  return Boolean(type) && type !== 'regular';
}

/**
 * Parses the share-cart tracking setting into `utm_*` key/value pairs.
 *
 * Accepts a query string (`utm_source=a&utm_medium=b`, optional leading `?`)
 * or one pair per line, as entered in a textarea setting.
 * A segment without `=` is read as a bare `utm_source` value, so legacy
 * single-value settings keep working. Non-`utm_*` keys are dropped so the
 * setting can never override `items`.
 *
 * @param {string} raw
 * @returns {Array<[string, string]>}
 */
function parseTrackingParams(raw) {
  /** @type {Array<[string, string]>} */
  const pairs = [];
  for (const segment of raw.replace(/^\?/, '').split(/[&\r\n]/)) {
    const part = segment.trim();
    if (!part) continue;
    const [entry] = new URLSearchParams(part.includes('=') ? part : `utm_source=${part}`);
    if (!entry) continue;
    const key = entry[0].trim().toLowerCase();
    const value = entry[1].trim();
    if (key.startsWith('utm_') && value) pairs.push([key, value]);
  }
  return pairs;
}

/**
 * Builds the share-cart URL for the given cart lines.
 *
 * Lines with a blank SKU or non-positive quantity are skipped rather than
 * rejected, so a caller that must not drop items (e.g. the TESO checkout, which
 * clears the cart before redirecting) has to validate its lines beforehand.
 *
 * @param {{ items?: Array<{ sku?: string | null, quantity?: number }> }} cart - Cart payload (or subset) shaped like /cart.js.
 * @param {{ baseUrl?: string, utmSource?: string }} [options] - `utmSource` is
 *   a bare utm_source value or a `utm_*` query string (see parseTrackingParams).
 * @returns {string} The share-cart URL. Returns '' when no line has both a
 *   usable SKU and a positive quantity, and also when `baseUrl` cannot be
 *   parsed as a URL — callers cannot infer the cause from '' alone.
 */
export function buildShareCartUrl(cart, { baseUrl = '', utmSource = '' } = {}) {
  const base = (String(baseUrl || '').trim() || DEFAULT_BASE_URL).replace(/\/$/, '');
  const tracking = parseTrackingParams(String(utmSource || '').trim());

  const segments = [];
  for (const item of cart?.items || []) {
    const sku = item.sku != null ? String(item.sku).trim() : '';
    if (!sku) continue;
    const quantity = Math.max(0, Math.floor(Number(item.quantity)) || 0);
    if (quantity <= 0) continue;
    segments.push(`${sku}:${quantity}`);
  }
  if (segments.length === 0) return '';

  let url;
  try {
    url = new URL(base);
  } catch (e) {
    try {
      url = new URL(base, location.origin);
    } catch (e2) {
      return '';
    }
  }
  url.searchParams.set('items', segments.join(','));
  for (const [key, value] of tracking) url.searchParams.set(key, value);
  return url.toString();
}

// Expose for classic (non-module) scripts, e.g. the kiosk inline script.
window.BunningsShareCart = window.BunningsShareCart || {};
window.BunningsShareCart.buildShareCartUrl = buildShareCartUrl;
window.BunningsShareCart.getLineCheckoutType = getLineCheckoutType;
window.BunningsShareCart.isTesoLine = isTesoLine;
window.BunningsShareCart.isThirdPartyCartLine = isThirdPartyCartLine;
window.BunningsShareCart.isExternalCheckoutLine = isExternalCheckoutLine;
